import { Link } from "react-router-dom";
import { useSpark } from "@/lib/store";
import { STAGES, activityPath } from "@/lib/activities";
import { SunArc } from "@/components/SparkSun";
import { ModeTag } from "@/components/ActivityMode";
import { NextAction } from "@/components/NextAction";
import { computeJourney, STATE_LABEL } from "@/lib/journey";

export { ACTIVITIES } from "@/lib/activities";

const StatusMark = ({ state }) => (
  <span
    className={`mt-2 h-[14px] w-[14px] shrink-0 rounded-full border ${
      state === "completed" ? "border-transparent" : state === "here" || state === "in-progress" ? "border-[#2C50A0]" : state === "next" ? "border-[#2C50A0]" : "border-[rgba(0,0,0,0.18)]"
    }`}
    style={{ background: state === "completed" ? "#2C50A0" : state === "here" || state === "in-progress" ? "hsl(var(--spark-warmth))" : "white" }}
    aria-label={STATE_LABEL[state] || "Not started"}
  />
);

export default function PartOne() {
  const { profile } = useSpark();
  const { items } = computeJourney(profile);

  return (
    <div className="space-y-16">
      <header className="relative">
        <div className="absolute -right-8 -top-12 opacity-[0.18] pointer-events-none hidden md:block">
          <SunArc size={240} color="#C3A56B" strokeWidth={1} />
        </div>
        <p className="spark-eyebrow-copper spark-eyebrow-with-dot">Part One</p>
        <h1 className="mt-3 text-4xl sm:text-5xl leading-tight text-[#17171C] font-medium tracking-tight">Find your direction.</h1>
        <p className="mt-6 max-w-2xl text-lg text-[#56565D] leading-relaxed">
          Move at your own pace. Most activities take under fifteen minutes, and none of them need
          finishing in one sitting. Each one is marked <span className="text-[#1F3D80]">Reflect</span>, <span className="text-[#17171C]">Notice</span> or <span className="text-[#17171C]">Act</span> so you know what kind of thinking it asks for.
          <span className="spark-voice"> Insight tends to arrive in small pieces — Spark will notice the patterns along the way.</span>
        </p>
      </header>

      <NextAction compact />

      <div className="spark-thread" />

      <div className="space-y-14">
        {STAGES.map((stage, si) => {
          const acts = items.filter(a => a.stage === stage.label);
          return (
            <section key={stage.key} data-testid={`part-one-stage-${stage.key}`} className="grid md:grid-cols-12 gap-6 md:gap-10">
              <div className="md:col-span-4">
                <p className="spark-eyebrow">{String(si + 1).padStart(2, "0")} · {stage.label}</p>
                <p className="mt-2 spark-voice text-2xl text-[#17171C] leading-snug">{stage.question}</p>
              </div>
              <ol className="md:col-span-8 divide-y divide-[rgba(0,0,0,0.08)] border-t border-[rgba(0,0,0,0.08)]">
                {acts.map(a => {
                  const state = a.state;
                  return (
                    <li key={a.key}>
                      <Link
                        to={activityPath(a)}
                        data-testid={`activity-card-${a.key}`}
                        data-state={state}
                        className={`group flex items-start gap-5 py-5 -mx-3 px-3 rounded-xl transition-colors ${state === "here" ? "bg-white/80" : "hover:bg-white/70"}`}
                      >
                        <StatusMark state={state} />
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                            <h3 className="text-xl sm:text-2xl leading-snug text-[#17171C] font-medium tracking-tight group-hover:text-[#1F3D80] transition-colors">{a.title}</h3>
                            <ModeTag mode={a.mode} />
                            <span className="text-xs text-[#8A8A91]">{a.minutes} min</span>
                            {STATE_LABEL[state] && (
                              <span data-testid={`activity-state-${a.key}`} className={`text-xs ${state === "here" ? "text-[#1F3D80] font-medium" : state === "completed" ? "text-[#1F3D80]" : "text-[#8A8A91]"}`}>{STATE_LABEL[state]}</span>
                            )}
                          </div>
                          <p className="mt-1 text-[#56565D] leading-relaxed">{a.blurb}</p>
                        </div>
                        <span className="text-[#8A8A91] group-hover:text-[#1F3D80] group-hover:translate-x-0.5 transition-all mt-2">→</span>
                      </Link>
                    </li>
                  );
                })}
              </ol>
            </section>
          );
        })}
      </div>

      <div className="spark-thread" />

      <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-5">
        <div>
          <p className="spark-eyebrow-copper mb-2">When you're ready</p>
          <p className="spark-voice text-3xl text-[#17171C] leading-snug">Okay. What's next?</p>
        </div>
        <Link to="/part-two" data-testid="part-one-part-two-btn" className="spark-btn-secondary">Make my move</Link>
      </div>
    </div>
  );
}
